import PropTypes from 'prop-types';
import { CityPropType, NewBookingDeliveryPropType } from 'common/prop-types';
import React, { useCallback, useEffect, useMemo, Fragment } from 'react';
import { connect } from 'react-redux';
import I18n from 'i18n-js';
import { Col, Popover, Row } from 'grab-ui';
import infoIcon from 'public/icons/info.svg';
import InputField from 'components/v2/input-field/input-field';
import SelectField from 'components/v2/select-field/select-field';
import { paymentMethodSelector } from 'selectors/booking-selectors';
import {
  isCashlessPaymentDisabledSelector,
  isCashPaymentDisabledSelector,
  isPaxSelector,
  selectedCitySelector,
  showEnterpriseTripInputSelector,
} from 'selectors/app-selector';
import { allDeliveriesSelector } from 'selectors/new-booking-selectors';
import _get from 'lodash/get';
import _sortBy from 'lodash/sortBy';
import {
  ADD_CASHLESS_ON_APP,
  ADD_CASHLESS_ON_GFB_PORTAL,
  CASH_BY_RECIPIENT,
  CASH_BY_SENDER,
  FIELD_PLACEHOLDER,
  PAYMENT_TYPE_GRABPAY,
  POSTPAID_BILLING_TYPE,
} from 'constants/booking-constants';
import { getAllPaxPaymentMethodsV2 } from 'actions/booking-actions';
import { showFailedPaymentsRetryModalAction } from 'actions/app-actions';
import { OfferConfigurationField } from 'components/v2/anytime-booking/common';
import paymentIcon from 'components/booking/payment-field/payment-icon';
import formatCurrency from 'common/utils/format-currency';
import calcPrice from 'common/utils/calc-price';
import cx from 'classnames';
import { getDeliveryFinalDropOffs } from 'helper/anytime-booking';
import { deliveryFormFields } from './common';
import styles from './styles.less';

const PaymentDetails = ({
  delivery,
  allDeliveries,
  form,
  paymentMethods,
  isPax,
  isCashPaymentDisabled,
  isCashlessPaymentDisabled,
  city,
  dispatch,
  index,
  showEnterpriseTripInput,
}) => {
  const config = _get(delivery, 'config');
  const deliveryDetail = _get(delivery, 'detail');
  const isMSD = useMemo(
    () => {
      const dropOffs = getDeliveryFinalDropOffs(deliveryDetail);
      return _get(dropOffs, 'length', 0) > 1;
    },
    [deliveryDetail],
  );

  const paymentTypeID = _get(
    deliveryDetail,
    deliveryFormFields.paymentTypeID.fieldName,
  );

  const serviceAllowCash = useMemo(
    () => _get(config, 'paymentMethods', ['CASH']).includes('CASH'),
    [config],
  );
  const serviceAllowCard = useMemo(
    () => _get(config, 'paymentMethods', []).includes('CARD'),
    [config],
  );
  const isSameDayService = useMemo(
    () => _get(deliveryDetail, 'configuration.serviceType') === 'SAME_DAY',
    [deliveryDetail],
  );

  const onRetryFailedPayments = useCallback(
    () => {
      dispatch(showFailedPaymentsRetryModalAction());
    },
    [dispatch],
  );

  const onReloadPaymentMethods = useCallback(
    () => {
      dispatch(getAllPaxPaymentMethodsV2(_get(city, 'countryCode')));
    },
    [dispatch, city],
  );

  const makeDisabledPaymentOption = useCallback(
    (option, reason, ctaText, onCTAClick) => (
      <Popover
        content={
          <div>
            <p className={styles.disabledReason}>{reason}</p>
            {ctaText &&
              onCTAClick && (
                <a className={styles.disabledCTA} onClick={onCTAClick}>
                  {ctaText}
                </a>
              )}
          </div>
        }
        trigger={'hover'}
        zIndex={10000}
        placement={'right'}
        overlayClassName={styles.disabledPopover}
      >
        {option}
      </Popover>
    ),
    [],
  );

  const paymentMethodOptions = useMemo(
    () => {
      if (!paymentMethods) return [];

      let anyCashlessPayment = false;
      const options = [];

      paymentMethods.forEach(paymentMethod => {
        const disabled = !paymentMethod.enabled || isCashlessPaymentDisabled;
        switch (paymentMethod.type) {
          case PAYMENT_TYPE_GRABPAY: {
            const isOVO = _get(city, 'countryCode') === 'ID';
            const isMOCA = _get(city, 'countryCode') === 'VN';

            let grabPayIcon;
            if (isOVO) {
              grabPayIcon = paymentIcon.OVO;
            } else if (isMOCA) {
              grabPayIcon = paymentIcon.MOCA;
            } else {
              grabPayIcon = paymentIcon.grabpay;
            }

            const balance = _get(paymentMethod, 'payload.Balance');
            const currency = _get(allDeliveries[0], 'quote.currency');
            if (!disabled) {
              anyCashlessPayment = true;
            }
            const totalAmount = calcPrice(
              allDeliveries.reduce(
                (totalAmount, delivery) =>
                  totalAmount + _get(delivery, 'quote.finalFare.lowerBound', 0),
                0,
              ),
              currency,
            );
            const notEnoughBalance = balance < totalAmount;
            const option = (
              <div
                className={cx(styles.paymentOption, {
                  [styles.disabled]: disabled,
                })}
              >
                <img src={grabPayIcon} alt="" />
                <span>
                  {paymentMethod &&
                    _get(paymentMethod, 'payload.BalanceErrorCode') === 0 &&
                    formatCurrency({
                      code: _get(paymentMethod, 'payload.Currency'),
                      amount: balance,
                    })}
                </span>
              </div>
            );
            options.push({
              value: paymentMethod.paymentTypeID.toString(),
              title: I18n.t(FIELD_PLACEHOLDER.GRAB_PAY),
              child: disabled
                ? makeDisabledPaymentOption(
                    option,
                    I18n.t('v2.failedPayments.bookingPopoverText'),
                    I18n.t('v2.failedPayments.bookingPopoverButton'),
                    onRetryFailedPayments,
                  )
                : notEnoughBalance
                  ? makeDisabledPaymentOption(
                      option,
                      I18n.t(
                        isOVO
                          ? 'ovo.insufficientBalance.popOverText'
                          : 'v2.failedPayments.grabPay.popOverText',
                      ),
                      I18n.t(
                        isOVO
                          ? 'ovo.insufficientBalance.bookingPopoverButton'
                          : 'v2.failedPayments.bookingPopoverButton',
                      ),
                      onReloadPaymentMethods,
                    )
                  : option,
              disabled: disabled || notEnoughBalance,
            });
            return;
          }
          case POSTPAID_BILLING_TYPE: {
            if (!disabled) {
              anyCashlessPayment = true;
            }
            const option = (
              <div
                className={cx(styles.paymentOption, {
                  [styles.disabled]: disabled,
                })}
              >
                <img src={paymentIcon.postpaidBilling} alt="" />
                <span>{I18n.t('booking.paymentMethod.corpBilling')}</span>
              </div>
            );
            options.push({
              value: paymentMethod.paymentTypeID.toString(),
              title: I18n.t(FIELD_PLACEHOLDER.CORP_BILLING),
              child: disabled
                ? makeDisabledPaymentOption(
                    option,
                    I18n.t('v2.failedPayments.bookingPopoverText'),
                    I18n.t('v2.failedPayments.bookingPopoverButton'),
                    onRetryFailedPayments,
                  )
                : option,
              disabled,
            });
            return;
          }
          default: {
            // credit cards
            if (serviceAllowCard) {
              const { type, refNumber } = paymentMethod;
              const accountIcon = _get(paymentMethod, 'payload.AccountIcon');
              if (
                type === POSTPAID_BILLING_TYPE ||
                type === PAYMENT_TYPE_GRABPAY
              ) {
                return;
              }
              if (!disabled) {
                anyCashlessPayment = true;
              }

              const option = (
                <div
                  className={cx(styles.paymentOption, {
                    [styles.disabled]: disabled,
                  })}
                >
                  <img
                    src={
                      accountIcon || paymentIcon[type] || paymentIcon.generic
                    }
                    alt=""
                  />
                  <span>{refNumber}</span>
                </div>
              );
              options.push({
                value: paymentMethod.paymentTypeID.toString(),
                title: refNumber,
                child: disabled
                  ? makeDisabledPaymentOption(
                      option,
                      I18n.t('v2.failedPayments.bookingPopoverText'),
                      I18n.t('v2.failedPayments.bookingPopoverButton'),
                      onRetryFailedPayments,
                    )
                  : option,
                disabled,
              });
            }
          }
        }
      });

      // cash
      if (serviceAllowCash && (isSameDayService || !isCashPaymentDisabled)) {
        options.push(
          {
            value: CASH_BY_SENDER,
            title: I18n.t('booking.paymentMethod.cashBySender'),
            child: (
              <div className={styles.paymentOption}>
                <img src={paymentIcon.CASH} alt="" />
                <span>{I18n.t('booking.paymentMethod.cashBySender')}</span>
              </div>
            ),
          },
          {
            value: CASH_BY_RECIPIENT,
            title: I18n.t('booking.paymentMethod.cashByRecipient'),
            child: (
              <div className={styles.paymentOption}>
                <img src={paymentIcon.CASH} alt="" />
                <span>{I18n.t('booking.paymentMethod.cashByRecipient')}</span>
              </div>
            ),
            disabled: isMSD,
          },
        );
      }

      if (!anyCashlessPayment) {
        if (isPax) {
          options.push({
            value: ADD_CASHLESS_ON_APP,
            title: I18n.t(
              'phrase.common.express_booking_flow_helpertext_use_grabapp_single_stop',
            ),
            child: (
              <div className={styles.paymentOption}>
                <img src={infoIcon} alt="" />
                <span>
                  {I18n.t(
                    'phrase.common.express_booking_flow_helpertext_use_grabapp_single_stop',
                  )}
                </span>
              </div>
            ),
            disabled: true,
          });
        } else {
          options.push({
            value: ADD_CASHLESS_ON_GFB_PORTAL,
            title: I18n.t(
              'phrase.common.express_booking_flow_helpertext_use_grabforbusiness_single_stop',
            ),
            child: (
              <div className={styles.paymentOption}>
                <img src={infoIcon} alt="" />
                <span>
                  {I18n.t(
                    'phrase.common.express_booking_flow_helpertext_use_grabforbusiness_single_stop',
                  )}
                </span>
              </div>
            ),
            disabled: true,
          });
        }
      }

      return _sortBy(options, p => p.disabled);
    },
    [
      city,
      isMSD,
      allDeliveries,
      paymentMethods,
      isCashPaymentDisabled,
      isCashlessPaymentDisabled,
      serviceAllowCash,
      serviceAllowCard,
      isSameDayService,
      isPax,
      makeDisabledPaymentOption,
      onReloadPaymentMethods,
      onRetryFailedPayments,
    ],
  );

  useEffect(
    () => {
      const validPaymentMethodOptions = paymentMethodOptions.filter(
        pmo => !pmo.disabled,
      );
      const firstValidPaymentMethodOption = validPaymentMethodOptions[0];

      // select first payment method
      if (config && !paymentTypeID && firstValidPaymentMethodOption) {
        form.setFieldsValue({
          [deliveryFormFields.paymentTypeID.fieldName]: _get(
            firstValidPaymentMethodOption,
            'value',
          ),
        });
      }

      // remove invalid selection
      if (paymentTypeID) {
        const selectedValid = validPaymentMethodOptions.find(
          pmo => pmo.value === paymentTypeID,
        );
        if (!selectedValid) {
          form.setFieldsValue({
            [deliveryFormFields.paymentTypeID.fieldName]: _get(
              firstValidPaymentMethodOption,
              'value',
            ),
          });
        }
      }
    },
    [form, paymentTypeID, paymentMethods, paymentMethodOptions],
  );

  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>
        {I18n.t('v2.homepage.manualOrder.heading.paymentDetails')}
      </div>
      <Row type="flex" gutter={16}>
        <Col xs={24} md={8}>
          <SelectField
            form={form}
            id={deliveryFormFields.paymentTypeID.fieldName}
            fieldOptions={{
              initialValue: _get(
                deliveryDetail,
                deliveryFormFields.paymentTypeID.fieldName,
              ),
              rules: deliveryFormFields.paymentTypeID.makeRules(),
            }}
            placeholder={I18n.t(
              'v2.homepage.manualOrder.placeholder.paymentMethod',
            )}
            options={paymentMethodOptions}
          />
        </Col>
        <Col xs={24} md={8}>
          <InputField
            form={form}
            id={deliveryFormFields.referenceCode.fieldName}
            fieldOptions={{
              initialValue: _get(
                deliveryDetail,
                deliveryFormFields.referenceCode.fieldName,
              ),
              rules: deliveryFormFields.referenceCode.makeRules(),
            }}
            placeholder={I18n.t(
              'v2.homepage.manualOrder.placeholder.referenceCode',
            )}
            fieldClassName={styles.inputField}
          />
        </Col>
        <Col xs={24} md={8}>
          <OfferConfigurationField delivery={delivery} index={index} />
        </Col>
        {showEnterpriseTripInput && (
          <Fragment>
            <Col xs={24} md={8} className={styles.tripInputField}>
              <InputField
                form={form}
                id={deliveryFormFields.tripCode.fieldName}
                fieldOptions={{
                  initialValue: _get(
                    deliveryDetail,
                    deliveryFormFields.tripCode.fieldName,
                  ),
                  rules: deliveryFormFields.tripCode.makeRules(),
                }}
                placeholder={I18n.t(
                  'v2.homepage.manualOrder.placeholder.tripCode',
                )}
                fieldClassName={styles.inputField}
              />
            </Col>
            <Col xs={24} md={8} className={styles.tripInputField}>
              <InputField
                form={form}
                id={deliveryFormFields.tripDescription.fieldName}
                fieldOptions={{
                  initialValue: _get(
                    deliveryDetail,
                    deliveryFormFields.tripDescription.fieldName,
                  ),
                  rules: deliveryFormFields.tripDescription.makeRules(),
                }}
                placeholder={I18n.t(
                  'v2.homepage.manualOrder.placeholder.tripDescription',
                )}
                fieldClassName={styles.inputField}
              />
            </Col>
          </Fragment>
        )}
      </Row>
    </div>
  );
};

PaymentDetails.defaultProps = {
  city: undefined,
  isCashPaymentDisabled: false,
  isCashlessPaymentDisabled: false,
  showEnterpriseTripInput: false,
};

PaymentDetails.propTypes = {
  delivery: NewBookingDeliveryPropType.isRequired,
  allDeliveries: PropTypes.arrayOf(NewBookingDeliveryPropType).isRequired,
  city: CityPropType,
  form: PropTypes.shape({}).isRequired,
  paymentMethods: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isPax: PropTypes.bool.isRequired,
  isCashPaymentDisabled: PropTypes.bool,
  isCashlessPaymentDisabled: PropTypes.bool,
  dispatch: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  showEnterpriseTripInput: PropTypes.bool,
};

export default connect(state => ({
  paymentMethods: paymentMethodSelector(state),
  isPax: isPaxSelector(state),
  city: selectedCitySelector(state),
  allDeliveries: allDeliveriesSelector(state),
  isCashPaymentDisabled: isCashPaymentDisabledSelector(state),
  isCashlessPaymentDisabled: isCashlessPaymentDisabledSelector(state),
  showEnterpriseTripInput: showEnterpriseTripInputSelector(state),
}))(PaymentDetails);
